import "zod/compile";
import { z } from "zod";

/**
 * Report color palettes: the colors every report canvas chart draws with.
 *
 * A palette holds three ranges, as Vega's `config.range` and every BI tool's
 * theme do:
 * - `categorical`: one color per series or category, in order (the first is
 *   a single-series chart's color);
 * - `sequential`: a ramp from low to high, for magnitude (heatmaps, color
 *   scales on a Table's column);
 * - `diverging`: a ramp through a light midpoint, for values either side of
 *   zero or a target.
 *
 * The organization picks one palette as its default; a Report may name
 * another. The built-in Gently palette is the default until one is picked.
 */

export const REPORT_COLOR_PALETTE_LIMITS = {
  categorical: { min: 2, max: 12 },
  sequential: { min: 2, max: 9 },
  diverging: { min: 3, max: 11 },
} as const;

export type ReportColorRange = keyof typeof REPORT_COLOR_PALETTE_LIMITS;
export const REPORT_COLOR_RANGES: readonly ReportColorRange[] = [
  "categorical",
  "sequential",
  "diverging",
];

/** A 6-digit hex color, e.g. "#1c1917". */
export const reportColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Must be a 6-digit hex color, like #1c1917.");

/** The three color ranges of a palette. */
export const reportColorPaletteColorsSchema = z
  .object({
    categorical: z
      .array(reportColorSchema)
      .min(REPORT_COLOR_PALETTE_LIMITS.categorical.min)
      .max(REPORT_COLOR_PALETTE_LIMITS.categorical.max),
    sequential: z
      .array(reportColorSchema)
      .min(REPORT_COLOR_PALETTE_LIMITS.sequential.min)
      .max(REPORT_COLOR_PALETTE_LIMITS.sequential.max),
    diverging: z
      .array(reportColorSchema)
      .min(REPORT_COLOR_PALETTE_LIMITS.diverging.min)
      .max(REPORT_COLOR_PALETTE_LIMITS.diverging.max),
  })
  .refine(
    (colors) =>
      new Set(colors.categorical.map((color) => color.toLowerCase())).size ===
      colors.categorical.length,
    {
      message: "Each categorical color can be used once.",
      path: ["categorical"],
    },
  );
export type ReportColorPaletteColors = z.infer<
  typeof reportColorPaletteColorsSchema
>;

/** The built-in palette's id; stored palettes have uuids. */
export const BUILTIN_REPORT_COLOR_PALETTE_ID = "gently";

/**
 * The built-in Gently palette. It follows DESIGN.md: Ink leads, so a
 * single-series chart is the app's warm near-black, and the neutrals are warm
 * stone. The hues after it are the Okabe-Ito set, darkened where needed to
 * reach 3:1 against white (WCAG 1.4.11, graphical objects), and chosen so every
 * pair stays apart under protanopia and deuteranopia; the palette passes
 * {@link checkReportColorPalette} with no issues.
 */
export const DEFAULT_REPORT_COLOR_PALETTE: ReportColorPaletteColors = {
  categorical: [
    "#1c1917", // ink
    "#0072b2", // blue
    "#c05600", // vermillion
    "#007a5a", // bluish green
    "#b04a8e", // reddish purple
    "#5b3a8e", // violet
    "#2897cf", // sky blue
    "#44403c", // stone-700, for "Other"
  ],
  // Warm stone to ink.
  sequential: [
    "#f5f5f4",
    "#d6d3d1",
    "#a8a29e",
    "#78716c",
    "#57534e",
    "#292524",
  ],
  // Blue through warm paper to vermillion: apart under every common color
  // vision deficiency, unlike red/green.
  diverging: ["#0b4f82", "#5b9bd5", "#f5f5f4", "#e58a4e", "#9a3b00"],
};

// ── Stored palettes ───────────────────────────────────────────────────────────

export const reportColorPaletteNameSchema = z.string().trim().min(1).max(80);

/** A palette as Settings and the Report editor list it. */
export const reportColorPaletteSchema = z.object({
  /** A uuid, or {@link BUILTIN_REPORT_COLOR_PALETTE_ID} for the built-in one. */
  id: z.string().min(1),
  name: z.string(),
  colors: reportColorPaletteColorsSchema,
  builtin: z.boolean(),
  /** The organization's default: the one a Report without its own uses. */
  isDefault: z.boolean(),
});
export type ReportColorPalette = z.infer<typeof reportColorPaletteSchema>;

/**
 * The palette a Report draws with: its own when it names one that exists,
 * else the organization's default, else the built-in one.
 */
export function resolveReportColorPalette(
  palettes: readonly ReportColorPalette[],
  paletteId?: string | null,
): ReportColorPaletteColors {
  const named = paletteId
    ? palettes.find((palette) => palette.id === paletteId)
    : undefined;
  return (
    named?.colors ??
    palettes.find((palette) => palette.isDefault)?.colors ??
    DEFAULT_REPORT_COLOR_PALETTE
  );
}

/** The `index`th categorical color, cycling past the end. */
export function categoricalColor(
  colors: Pick<ReportColorPaletteColors, "categorical">,
  index: number,
): string {
  const { categorical } = colors;
  const at =
    ((index % categorical.length) + categorical.length) % categorical.length;
  return categorical[at] ?? DEFAULT_REPORT_COLOR_PALETTE.categorical[0] ?? "";
}

// ── Checks ────────────────────────────────────────────────────────────────────

/**
 * An advisory finding about a palette; none of them blocks saving it (the
 * schema's rules do that). `indexes` are positions in `range`.
 * - `low-contrast`: a categorical color under 3:1 against white.
 * - `indistinct`: two categorical colors too alike, for everyone or under a
 *   simulated color vision deficiency (`vision`).
 * - `not-monotonic`: a sequential ramp whose lightness doesn't run one way.
 * - `midpoint-not-lightest`: a diverging ramp whose middle isn't its lightest.
 */
export type ReportColorPaletteIssue =
  | {
      code: "low-contrast";
      range: "categorical";
      indexes: [number];
      contrast: number;
    }
  | {
      code: "indistinct";
      range: "categorical";
      indexes: [number, number];
      vision: ColorVision;
    }
  | { code: "not-monotonic"; range: "sequential" | "diverging" }
  | { code: "midpoint-not-lightest"; range: "diverging" };

export type ColorVision = "typical" | "protanopia" | "deuteranopia";

/** WCAG 1.4.11's minimum for graphical objects. */
export const REPORT_COLOR_MIN_CONTRAST = 3;
/** The least CIE76 distance two series colors keep, typical or simulated. */
export const REPORT_COLOR_MIN_DISTANCE = 12;

type Rgb = [number, number, number];

function rgb(hex: string): Rgb {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearRgb(hex: string): Rgb {
  const [r, g, b] = rgb(hex);
  return [toLinear(r), toLinear(g), toLinear(b)];
}

function luminance([r, g, b]: Rgb): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The WCAG contrast ratio of two hex colors, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(linearRgb(a));
  const lb = luminance(linearRgb(b));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// Machado, Oliveira & Fernandes (2009), severity 1.0, applied in linear RGB.
const VISION_MATRICES: Record<Exclude<ColorVision, "typical">, number[][]> = {
  protanopia: [
    [0.152286, 1.052583, -0.204868],
    [0.114503, 0.786281, 0.099216],
    [-0.003882, -0.048116, 1.051998],
  ],
  deuteranopia: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.01182, 0.04294, 0.968881],
  ],
};

function simulate(color: Rgb, vision: ColorVision): Rgb {
  if (vision === "typical") return color;
  const matrix = VISION_MATRICES[vision];
  const clamp = (value: number) => Math.min(1, Math.max(0, value));
  const row = (index: number) => {
    const [a = 0, b = 0, c = 0] = matrix[index] ?? [];
    return clamp(a * color[0] + b * color[1] + c * color[2]);
  };
  return [row(0), row(1), row(2)];
}

/** CIE L*a*b* (D65) of a linear-RGB color. */
function lab([r, g, b]: Rgb): Rgb {
  const x = (0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047;
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const z = (0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883;
  const f = (t: number) =>
    t > 216 / 24389 ? Math.cbrt(t) : (841 / 108) * t + 4 / 29;
  const [fx, fy, fz] = [f(x), f(y), f(z)];
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/** Perceived lightness, L* from 0 (black) to 100 (white). */
export function lightness(hex: string): number {
  return lab(linearRgb(hex))[0];
}

/** How far apart two colors look (CIE76 ΔE), as `vision` sees them. */
export function colorDistance(
  a: string,
  b: string,
  vision: ColorVision = "typical",
): number {
  const [l1, a1, b1] = lab(simulate(linearRgb(a), vision));
  const [l2, a2, b2] = lab(simulate(linearRgb(b), vision));
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}

const VISIONS: readonly ColorVision[] = [
  "typical",
  "protanopia",
  "deuteranopia",
];

function monotonic(values: readonly number[]): boolean {
  const rising = values.every(
    (value, i) => i === 0 || value >= (values[i - 1] ?? value),
  );
  const falling = values.every(
    (value, i) => i === 0 || value <= (values[i - 1] ?? value),
  );
  return rising || falling;
}

/**
 * The advisory checks every palette gets in Settings: each categorical color
 * readable against a white chart, series colors apart from each other for
 * people with typical vision and with the two common red-green deficiencies,
 * a sequential ramp that runs one way, and a diverging ramp whose middle is
 * its lightest color with each half running one way.
 */
export function checkReportColorPalette(
  colors: ReportColorPaletteColors,
): ReportColorPaletteIssue[] {
  const issues: ReportColorPaletteIssue[] = [];
  colors.categorical.forEach((color, index) => {
    const contrast = contrastRatio(color, "#ffffff");
    if (contrast < REPORT_COLOR_MIN_CONTRAST)
      issues.push({
        code: "low-contrast",
        range: "categorical",
        indexes: [index],
        contrast: Math.round(contrast * 100) / 100,
      });
  });
  for (let i = 0; i < colors.categorical.length; i++)
    for (let j = i + 1; j < colors.categorical.length; j++) {
      const [a = "", b = ""] = [colors.categorical[i], colors.categorical[j]];
      const vision = VISIONS.find(
        (candidate) =>
          colorDistance(a, b, candidate) < REPORT_COLOR_MIN_DISTANCE,
      );
      if (vision)
        issues.push({
          code: "indistinct",
          range: "categorical",
          indexes: [i, j],
          vision,
        });
    }
  if (!monotonic(colors.sequential.map(lightness)))
    issues.push({ code: "not-monotonic", range: "sequential" });
  const diverging = colors.diverging.map(lightness);
  const middle = Math.floor((diverging.length - 1) / 2);
  const upper = Math.ceil((diverging.length - 1) / 2);
  const mid = Math.max(diverging[middle] ?? 0, diverging[upper] ?? 0);
  if (diverging.some((value) => value > mid + 0.5))
    issues.push({ code: "midpoint-not-lightest", range: "diverging" });
  else if (
    !monotonic(diverging.slice(0, middle + 1)) ||
    !monotonic(diverging.slice(upper))
  )
    issues.push({ code: "not-monotonic", range: "diverging" });
  return issues;
}
